import React, { useState } from "react";
import tw, { styled } from "twin.macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import AnimationRevealPage from "helpers/AnimationRevealPage";
import VectorDesign from "images/Vector1.svg";
import GrayDesign from "images/blueDots.svg";
import LineDesign from "images/greenLines.png";

// ==========================
// Styled Components
// ==========================

const Section = tw.section`
  bg-gradient-to-br from-white to-gray-100 py-20 px-4 relative overflow-hidden
`;

const Container = tw.div`
  max-w-6xl mx-auto
`;

const HeadingWrap = tw.div`
  text-center mb-16
`;

const Heading = tw.h1`
  text-4xl md:text-5xl font-bold text-gray-800 mb-4
`;

const SubText = tw.p`
  text-xl text-gray-600 max-w-2xl mx-auto
`;

const Vector = tw.img`
  w-20 mx-auto mb-4
`;

const VideoGrid = tw.div`
  grid md:grid-cols-3 gap-8 mb-16
`;

const VideoCard = tw.div`
  bg-white rounded-2xl shadow-lg overflow-hidden
  hover:shadow-2xl transition-shadow duration-300
`;

const ThumbnailContainer = tw.div`
  relative
`;

const Thumbnail = tw.img`
  w-full h-auto object-cover
`;

const Overlay = tw.div`
  absolute inset-0 bg-black bg-opacity-0 
  hover:bg-opacity-25 transition-all duration-300 
  flex items-center justify-center
`;

const WatchButton = tw.button`
  bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold 
  opacity-0 hover:opacity-100 transition duration-300
`;

const VideoContent = tw.div`
  p-6
`;

const VideoTitle = tw.h4`
  text-xl font-bold text-gray-800 mb-2
`;

const VideoDesc = tw.p`
  text-gray-600 text-sm mb-4
`;

const VideoLink = tw.a`
  text-blue-600 hover:text-blue-800 font-semibold text-sm
`;

const SubscribeBox = tw.div`
  text-center mt-12
`;

const SubscribeText = tw.p`
  text-lg text-gray-600 mb-4
`;

const SubscribeButton = tw.a`
  inline-block bg-[#3f6c68] hover:bg-[#355a56] text-white font-bold
  py-4 px-8 rounded-full text-lg transition-colors duration-300
  shadow-lg hover:shadow-xl 
`;

const FloatingImg = styled.img(({ direction }) => [
  tw`absolute opacity-75 z-0`,
  direction === "left" && tw`top-[150px] left-0`,
  direction === "right" && tw`bottom-[60px] right-[60px]`,
]);

// ==========================
// Component
// ==========================

const FreeVideosSection = () => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const x = e.clientX - window.innerWidth / 2;
    const y = e.clientY - window.innerHeight / 2;
    setMouse({ x, y });
  };

  const demoVideos = [
    {
      title: "CA-Intermediate Taxation | Sep 2025 Exams | RTP Discussion",
      description:
        "Detailed discussion on RTP released by ICAI for September 2025 Exams.",
      videoUrl: "https://www.youtube.com/watch?v=3kl9sdNtF6Y",
      thumbnail: "https://i.ytimg.com/vi_webp/3kl9sdNtF6Y/maxresdefault.webp",
    },
    {
      title: "CA-Inter Taxation | Amendments (DT + GST)",
      description: "Complete amendments applicable for Sep 2025 Exams.",
      videoUrl: "https://www.youtube.com/watch?v=cld0IJXvgmI",
      thumbnail: "https://i.ytimg.com/vi_webp/cld0IJXvgmI/maxresdefault.webp",
    },
    {
      title: "DT Revision – Topic 12: TDS & TCS",
      description: "Revisionary Marathon based on PY 2024-25 AY 2025-26.",
      videoUrl: "https://www.youtube.com/watch?v=DC6PYxEv_SA",
      thumbnail: "https://i.ytimg.com/vi_webp/DC6PYxEv_SA/maxresdefault.webp",
    },
    {
      title: "DT Revision – Topic 1: Basic Concepts",
      description: "Revision of Basic Concepts for May/Sep 2025 exams.",
      videoUrl: "https://www.youtube.com/watch?v=z0P4QyA9EHk",
      thumbnail: "https://i.ytimg.com/vi_webp/z0P4QyA9EHk/maxresdefault.webp",
    },
    {
      title: "DT Revision – Topic 2: Residential Status",
      description: "Detailed lecture on Residential Status.",
      videoUrl: "https://www.youtube.com/watch?v=sWzndVcn6kg&t=1s",
      thumbnail: "https://i.ytimg.com/vi_webp/sWzndVcn6kg/maxresdefault.webp",
    },
    {
      title: "DT Revision – Topic 3: Salary",
      description: "Complete Salary chapter explained in detail.",
      videoUrl: "https://www.youtube.com/watch?v=0W_1yoQnqtE",
      thumbnail: "https://i.ytimg.com/vi_webp/0W_1yoQnqtE/maxresdefault.webp",
    },
  ];

  return (
    <AnimationRevealPage>
      <Header />

      <Section onMouseMove={handleMouseMove}>
        {/* LEFT FLOAT IMAGE */}
        <FloatingImg
          src={GrayDesign}
          direction="left"
          style={{
            transform: `translate(${mouse.x * 0.03}px, ${mouse.y * 0.03}px)`,
          }}
        />

        {/* RIGHT FLOAT IMAGE */}
        <FloatingImg
          src={LineDesign}
          direction="right"
          style={{
            transform: `translate(${mouse.x * -0.03}px, ${mouse.y * -0.03}px)`,
          }}
        />

        <Container>
          {/* Top Heading */}
          <HeadingWrap>
            <Heading>Free Demo Videos</Heading>
            <Vector src={VectorDesign} />
            <SubText>
              Master tax the easy way. Explore our premium content with these
              exclusive free videos and learn from the best in the field.
            </SubText>
          </HeadingWrap>

          {/* Videos */}
          <VideoGrid>
            {demoVideos.map((video, index) => (
              <VideoCard key={index}>
                <ThumbnailContainer>
                  <Thumbnail src={video.thumbnail} alt={video.title} />
                  <Overlay>
                    <WatchButton
                      onClick={() => window.open(video.videoUrl, "_blank")}
                    >
                      Watch Now
                    </WatchButton>
                  </Overlay>
                </ThumbnailContainer>

                <VideoContent>
                  <VideoTitle>{video.title}</VideoTitle>
                  <VideoDesc>{video.description}</VideoDesc>
                  <VideoLink href={video.videoUrl} target="_blank">
                    View on YouTube →
                  </VideoLink>
                </VideoContent>
              </VideoCard>
            ))}
          </VideoGrid>

          {/* Subscribe */}
          <SubscribeBox>
            <SubscribeText>
              Don’t miss out on expert guidance and free learning resources.
              Subscribe now and keep learning with us.
            </SubscribeText>
            <SubscribeButton
              href="https://www.youtube.com/@TaxwithoutTears"
              target="_blank"
            >
              View Youtube Channel
            </SubscribeButton>
          </SubscribeBox>
        </Container>
      </Section>

      <Footer />
    </AnimationRevealPage>
  );
};

export default FreeVideosSection;
